import { useState, useEffect, useMemo } from "react";
import { COI_ELIGIBLE_STATUSES, US_STATES } from "@shared/schema";
import type { Policy } from "@shared/schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  useCreateWaiverOfSubrogation,
  useUpdateWaiverOfSubrogation,
  useDeleteWaiverOfSubrogation,
  type WaiverOfSubrogationLink,
} from "@/lib/api";
import {
  buildPolicyCoverageGroups,
  countOfferedSelections,
  toggleOfferedSelection,
} from "@/lib/policy-coverages";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Loader2, Trash2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insuredId: string;
  policies?: Policy[];
  editData?: WaiverOfSubrogationLink | null;
}

interface FormErrors {
  name?: string;
  email?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  coverages?: string;
  form?: string;
}

export function WaiverOfSubrogationDialog({
  open,
  onOpenChange,
  insuredId,
  policies = [],
  editData,
}: Props) {
  const { toast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const createMutation = useCreateWaiverOfSubrogation(insuredId);
  const updateMutation = useUpdateWaiverOfSubrogation(insuredId);
  const deleteMutation = useDeleteWaiverOfSubrogation(insuredId);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [specialVerbiage, setSpecialVerbiage] = useState("");
  const [selectedCoverages, setSelectedCoverages] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});

  const isEdit = !!editData;

  const coverageGroups = useMemo(
    () => buildPolicyCoverageGroups(policies, { eligibleStatuses: COI_ELIGIBLE_STATUSES }),
    [policies],
  );
  const allCoverageIds = useMemo(
    () => coverageGroups.flatMap((g) => g.coverages.map((c) => c.id)),
    [coverageGroups],
  );
  // CSR-2148 — a saved waiver may name a coverage the carrier has since
  // removed; it is kept in the payload but no longer offered, so count what is
  // on offer.
  const selectedOfferedCount = useMemo(
    () => countOfferedSelections(selectedCoverages, allCoverageIds),
    [selectedCoverages, allCoverageIds],
  );

  useEffect(() => {
    if (open) {
      if (editData) {
        setName(editData.name || "");
        setEmail(editData.email || "");
        setStreet(editData.street || "");
        setCity(editData.city || "");
        setState(editData.state || "");
        setZip(editData.zip || "");
        setSpecialVerbiage(editData.specialVerbiage || "");
        setSelectedCoverages(
          Array.isArray(editData.coverageIds) ? editData.coverageIds.map(String) : [],
        );
      } else {
        setName("");
        setEmail("");
        setStreet("");
        setCity("");
        setState("");
        setZip("");
        setSpecialVerbiage("");
        setSelectedCoverages([]);
      }
      setErrors({});
    }
  }, [open, editData]);

  const toggleCoverage = (id: string) => {
    setSelectedCoverages((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const selectAllCoverages = () => {
    setSelectedCoverages(toggleOfferedSelection(selectedCoverages, allCoverageIds));
  };

  const ZIP_REGEX = /^\d{5}(-\d{4})?$/;

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (email.trim() && !/\S+@\S+\.\S+/.test(email.trim()))
      newErrors.email = "Email must be a valid email address";
    if (!street.trim()) newErrors.street = "Street is required";
    if (!city.trim()) newErrors.city = "City is required";
    if (!state) newErrors.state = "State is required";
    if (!zip.trim()) newErrors.zip = "ZIP is required";
    else if (!ZIP_REGEX.test(zip.trim()))
      newErrors.zip = "ZIP must be 5 digits or 5+4 (e.g. 12345 or 12345-6789)";
    // CSR-2148 — count what the picker offers: a waiver retained against a
    // removed coverage must not satisfy a rule about what the user can see.
    if (selectedOfferedCount === 0)
      newErrors.coverages = "At least one coverage is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      name: name.trim(),
      email: email.trim() || null,
      street: street.trim(),
      city: city.trim(),
      state,
      zip: zip.trim(),
      specialVerbiage: specialVerbiage.trim() || null,
      coverageIds: selectedCoverages,
    };

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ wosId: editData!.id, data: payload });
        toast({ title: "Updated", description: "Waiver of Subrogation updated successfully." });
      } else {
        await createMutation.mutateAsync(payload);
        toast({ title: "Added", description: "Waiver of Subrogation added successfully." });
      }
      onOpenChange(false);
    } catch (err) {
      const message = (err as Error).message || "Something went wrong. Please try again.";
      if (/already exists/i.test(message)) {
        setErrors((prev) => ({ ...prev, name: message }));
      } else {
        setErrors((prev) => ({ ...prev, form: message }));
      }
    }
  };

  const handleDelete = async () => {
    if (!editData) return;
    const ok = await confirm({
      title: "Remove Waiver of Subrogation",
      description: `Remove "${editData.name}" as a Waiver of Subrogation?`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;

    try {
      await deleteMutation.mutateAsync(editData.id);
      toast({ title: "Deleted", description: "Waiver of Subrogation removed." });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Error",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const isBusy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="waiver-of-subrogation-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Waiver of Subrogation" : "Add Waiver of Subrogation"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the waiver of subrogation details and the coverages it applies to."
              : "Add a waiver of subrogation to this insured."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium">
              Name <span className="text-destructive">*</span>
            </label>
            <Input
              className="mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. ABC Logistics Inc."
              data-testid="wos-dialog-name"
            />
            {errors.name && (
              <p className="text-xs text-destructive mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Email</label>
            <Input
              className="mt-1"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@example.com"
              data-testid="wos-dialog-email"
            />
            {errors.email && (
              <p className="text-xs text-destructive mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">
              Street <span className="text-destructive">*</span>
            </label>
            <Input
              className="mt-1"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder="123 Main St"
              data-testid="wos-dialog-street"
            />
            {errors.street && (
              <p className="text-xs text-destructive mt-1">{errors.street}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium">
                City <span className="text-destructive">*</span>
              </label>
              <Input
                className="mt-1"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                data-testid="wos-dialog-city"
              />
              {errors.city && (
                <p className="text-xs text-destructive mt-1">{errors.city}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">
                State <span className="text-destructive">*</span>
              </label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                value={state}
                onChange={(e) => setState(e.target.value)}
                data-testid="wos-dialog-state"
              >
                <option value="">Select...</option>
                {US_STATES.map((st) => (
                  <option key={st.value} value={st.value}>
                    {st.value} — {st.label}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="text-xs text-destructive mt-1">{errors.state}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">
                ZIP <span className="text-destructive">*</span>
              </label>
              <Input
                className="mt-1"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="12345"
                inputMode="numeric"
                pattern="\d{5}(-\d{4})?"
                maxLength={10}
                data-testid="wos-dialog-zip"
              />
              {errors.zip && (
                <p className="text-xs text-destructive mt-1">{errors.zip}</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Special Verbiage</label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Optional free-form text to include on certificates issued with this Waiver of Subrogation.
            </p>
            <textarea
              className="mt-1 w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              value={specialVerbiage}
              onChange={(e) => setSpecialVerbiage(e.target.value)}
              placeholder="e.g. Waiver of Subrogation per contract..."
              data-testid="wos-dialog-verbiage"
            />
          </div>

          <div>
            <label className="text-sm font-medium">
              Coverages <span className="text-destructive">*</span>
            </label>
            <p className="text-xs text-muted-foreground mt-0.5">
              At least one coverage is required.
            </p>
            {coverageGroups.length === 0 ? (
              <p className="text-xs italic text-muted-foreground mt-2">
                No active policies with coverages on this insured.
              </p>
            ) : (
              <div className="mt-2 rounded-md border border-input overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-input">
                  <span className="text-xs text-muted-foreground">
                    {selectedOfferedCount} of {allCoverageIds.length} selected
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs px-2"
                    onClick={selectAllCoverages}
                  >
                    {selectedOfferedCount === allCoverageIds.length
                      ? "Deselect all"
                      : "Select all"}
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {coverageGroups.map((group) => (
                    <div key={group.policyId}>
                      <div className="px-3 py-1.5 bg-muted/20 border-b border-input">
                        <span className="text-xs font-semibold text-muted-foreground uppercase">
                          {group.policyLabel}
                        </span>
                      </div>
                      {group.coverages.map((cov) => (
                        <label
                          key={cov.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-muted/30 cursor-pointer text-sm"
                        >
                          <Checkbox
                            checked={selectedCoverages.includes(cov.id)}
                            onCheckedChange={() => toggleCoverage(cov.id)}
                          />
                          {cov.name}
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {errors.coverages && (
              <p className="text-xs text-destructive mt-1">{errors.coverages}</p>
            )}
          </div>
        </div>

        {errors.form && (
          <p className="text-xs text-destructive" data-testid="wos-dialog-form-error">
            {errors.form}
          </p>
        )}

        <DialogFooter className="flex justify-between sm:justify-between">
          {isEdit ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5"
              onClick={handleDelete}
              disabled={isBusy}
              data-testid="wos-dialog-delete"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isBusy}
              data-testid="wos-dialog-submit"
            >
              {isBusy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  {isEdit ? "Saving..." : "Adding..."}
                </>
              ) : isEdit ? (
                "Save"
              ) : (
                "Add"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {confirmDialog}
    </>
  );
}
